import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Role } from 'src/app/models/shared/role.model';
import { Marker } from 'src/app/models/shared/marker.model';
import { PageRequest, PaginationResult } from 'src/app/lib/ngx-crud.service';
import { UtilsService } from 'src/app/services/utils.service';
import { MarkersService } from 'src/app/services/markers.service';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit, OnDestroy {

  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild('detailsModal', { static: true }) detailsModal!: TemplateRef<any>;

  searchForm!: FormGroup;
  searchSubscription!: Subscription;

  dataSource: MatTableDataSource<Marker> = new MatTableDataSource<Marker>();

  displayedColumns: string[] = ['id', 'name', 'types', 'subtypes', 'coordinates', 'dettagli'];
  editItemForm!: FormGroup;
  editItem!: Marker;
  dialogRef!: MatDialogRef<any, any>;

  types: any[] = [];
  tipi: any[] = [];
  sottotipi: any[] = [];

  protected _onDestroy = new Subject<void>();

  constructor(
    public utilsService: UtilsService,
    private dialog: MatDialog,
    private markersService: MarkersService,
  ) {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnInit(): void {
    this.searchForm = new FormGroup({
      name: new FormControl(),
      types: new FormControl(),
      subtypes: new FormControl(),
      coordinates: new FormControl(),
    });
    this.editItemForm = new FormGroup({
      id: new FormControl(),
      attivo: new FormControl(),
      name: new FormControl(),
      types: new FormControl(),
      subtypes: new FormControl(),
      coordinates: new FormControl(),
      metadata: new FormControl(),
      indirizzo: new FormControl(),
      cap: new FormControl(),
      municipio: new FormControl(),
      località: new FormControl(),
      distretto: new FormControl(),
      comune: new FormControl(),
      provincia: new FormControl(),
      telefono: new FormControl(),
      email: new FormControl(),
      partitaIVA: new FormControl(),
      capacitàRicettiva: new FormControl(),
      classeUtenza: new FormControl(),
      indirizzoCompleto: new FormControl(),
      googleMaps: new FormControl(),
      numeroAccreditamento: new FormControl(),
      scadenzaAccreditamento: new FormControl(),
    });

    this.initAutocomplete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initSort()
      this.initPaginator()
      this.search()
    })
    this.loadType();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
  }

  initSort() {
    this.sort.active = "id";
    this.sort.direction = "desc";
    this.sort.sortChange.subscribe(() => {
      if (!this.dataSource.data.length) return
      this.paginator.pageIndex = 0
      this.search()
    })
  }

  initPaginator() {
    this.paginator.pageIndex = 0
    this.paginator.pageSize = 25
    this.paginator.page.subscribe(() => {
      this.search(true)
    })
  }

  initAutocomplete() {
    let dummy;
  }

  loadType() {
    this.markersService.getLayers().subscribe((lls) => {
      lls.sort((a, b) => a.type.localeCompare(b.type))
      lls.forEach((l) => {
        l.subtypes.sort((a: any, b: any) => a.subtype.localeCompare(b.subtype))
      })
      this.types = lls
      this.tipi = lls.map((l) => l.type).filter((v, i, a) => a.indexOf(v) === i)
      this.sottotipi = lls.map((l) => l.subtypes).flat().map((l) => l.subtype).filter((v, i, a) => a.indexOf(v) === i)
    });
  }

  search(keepPage?: boolean) {
    if (!keepPage) this.paginator.pageIndex = 0
    let raw = this.searchForm.getRawValue();

    if (raw.name) raw.name = { $like: raw.name.trim().toUpperCase() }
    if (raw.types) raw.types = { $like: raw.types }
    if (raw.subtypes) raw.subtypes = { $like: raw.subtypes }

    let sortProperty: string = this.sort.active
    let pageRequest: PageRequest = new PageRequest(this.paginator.pageIndex, this.paginator.pageSize, this.sort.direction, sortProperty)

    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe()
    }
    this.searchSubscription = this.markersService.search(raw, pageRequest).subscribe(
      res => {
        this.populateTable(res)
      },
      err => {
        this.utilsService.defaultHttpErrorHandler(err)
      }
    )
  }

  clear() {
    this.searchForm.reset()
    this.sort.active = "id";
    this.sort.direction = "desc";
    this.search()
  }

  populateTable(result: PaginationResult<Marker>) {
    this.dataSource.data = result.items.map((item: any) => new Marker(item))
    let interval = setInterval(() => {
      if (this.paginator) {
        this.paginator.length = result.total
        clearInterval(interval)
      }
    }, 200)
  }

  new() {
    this.edit(new Marker())
  }

  edit(item: Marker) {
    this.editItemForm.reset();
    this.editItemForm.patchValue(item);
    this.editItem = item;
    if ((item as any).metadata) {
      this.editItemForm.patchValue({
        indirizzo: (item as any).metadata.Indirizzo,
        cap: (item as any).metadata.CAP,
        municipio: (item as any).metadata.Municipio,
        località: (item as any).metadata.Località,
        distretto: (item as any).metadata.Distretto,
        comune: (item as any).metadata.Comune,
        provincia: (item as any).metadata.Provincia,
        telefono: (item as any).metadata.Telefono,
        email: (item as any).metadata.Email,
        partitaIVA: (item as any).metadata?.['Partita IVA'],
        capacitàRicettiva: (item as any).metadata?.['Capacità ricettiva'],
        classeUtenza: (item as any).metadata?.['Classe di Utenza'],
        indirizzoCompleto: (item as any).metadata?.['Indirizzo completo'],
        googleMaps: (item as any).metadata?.['Google Maps'],
        attivo: typeof (item as any).metadata?.Attivo === 'undefined' ? true : (item as any).metadata?.Attivo,
        numeroAccreditamento: (item as any).metadata?.['Numero Accreditamento'],
        scadenzaAccreditamento: (item as any).metadata?.['Scadenza Accreditamento'],
      });
    }
    this.dialogRef = this.dialog.open(this.detailsModal);
  }

  save() {
    let raw = this.editItemForm.getRawValue();
    raw.metadata = {
      Indirizzo: raw.indirizzo,
      Attivo: raw.attivo,
      ['Numero Accreditamento']: raw.numeroAccreditamento,
      ['Scadenza Accreditamento']: raw.scadenzaAccreditamento,
      CAP: raw.cap,
      Municipio: raw.municipio,
      Località: raw.località,
      Distretto: raw.distretto,
      Comune: raw.comune,
      Provincia: raw.provincia,
      Telefono: raw.telefono,
      Email: raw.email,
      'Partita IVA': raw.partitaIVA,
      'Capacità ricettiva': raw.capacitàRicettiva,
      'Classe di Utenza': raw.classeUtenza,
      'Indirizzo completo': raw.indirizzoCompleto,
      'Google Maps': raw.googleMaps,
    }
    if (raw.coordinates && typeof raw.coordinates === 'string') {
      raw.coordinates = raw.coordinates.replace(/\[/g, '').replace(/\]/g, '');
      raw.coordinates = "[" + raw.coordinates + "]";
    }
    this.markersService.save(raw).subscribe(res => {
      const marker = new Marker(res);
      this.editItemForm.patchValue(marker);
      this.utilsService.notificationsService.success('Marker inserito correttamente');
      setTimeout(() => {
        this.search(true);
      }, 100);
    }, err => {
      console.log(err)
      this.utilsService.defaultHttpErrorHandler(err);
    });
  }

  delete() {
    let raw = this.editItemForm.getRawValue();
    this.markersService.delete(raw.id).subscribe(res => {
      this.utilsService.notificationsService.success('Marker eliminato con successo');
      this.dialogRef.close();
      setTimeout(() => {
        this.search(true);
      }, 100);
    }, err => {
      console.log(err)
      this.utilsService.defaultHttpErrorHandler(err);
    })
  }
}
